import { config } from '../config';

// Use frontend URL (proxies to API, enables SSO)
const AUTH_URL = config.authServiceUrl || 'https://auth.inite.ai';
const CLIENT_ID = 'smart-chat-admin';
const REDIRECT_URI = `${window.location.origin}/auth-callback`;

/**
 * OAuth Service - все OAuth операции в одном месте
 * 
 * SOLID: Single Responsibility - только OAuth логика
 */

// PKCE helpers
const generateRandom = (length: number): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
};

const base64urlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  return btoa(String.fromCharCode.apply(null, Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const oauthService = {
  /**
   * Начать OAuth flow - редирект на auth-service
   */
  startLogin: async (): Promise<void> => {
    const codeVerifier = generateRandom(32);
    const state = generateRandom(16);
    
    // Сохраняем для callback
    sessionStorage.setItem('oauth_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);
    
    // Генерируем code_challenge
    const hash = await sha256(codeVerifier);
    const codeChallenge = base64urlEncode(hash);
    
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: 'openid profile email admin',
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    
    window.location.href = `${AUTH_URL}/oauth/authorize?${params}`;
  },

  /**
   * Обработать callback - обменять code на tokens
   */
  handleCallback: async (code: string, state: string): Promise<void> => {
    const savedState = sessionStorage.getItem('oauth_state');
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
    
    if (state !== savedState) {
      throw new Error('State mismatch');
    }
    
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }
    
    // Обмениваем code на tokens
    const response = await fetch(`${AUTH_URL}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'authorization_code',
        code,
        code_verifier: codeVerifier,
        redirect_uri: REDIRECT_URI,
        client_id: CLIENT_ID,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Token exchange failed');
    }
    
    const tokens = await response.json();
    
    // Сохраняем токен
    localStorage.setItem('token', tokens.access_token);
    
    // Декодируем ID token для user info
    const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
    
    // Проверяем admin роль
    if (!payload.roles?.includes('admin')) {
      localStorage.removeItem('token');
      throw new Error('Требуются права администратора');
    }
    
    // Сохраняем user info
    localStorage.setItem('user', JSON.stringify({
      id: payload.sub,
      email: payload.email,
      name: payload.name,
      roles: payload.roles,
      avatarUrl: payload.picture,
    }));
    
    // Очищаем PKCE данные
    sessionStorage.removeItem('oauth_code_verifier');
    sessionStorage.removeItem('oauth_state');
  },

  /**
   * Logout - очистка и редирект
   */
  logout: (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
};

